import { Dispatch, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateMethods } from '@utils/dates/dateMethods';
import { api } from '../../common-utils-services/axiosUtil';
import {
    API,
    ApplicationType,
    CsrOptionName,
    IEligibilityDateDetails,
    IGetSepDetails,
    IProgramEligibility,
    ISepApplicantsEvents,
    ISepDetails,
    REASON_CODE,
    RequestStatus,
    SepDateType,
    YesNo,
    convertDateFormat,
    endpoints,
    getApplicantEvents,
    getSepDate,
    setError,
    startSpinner,
    stopSpinner,
    useAppDispatch,
    useAppSelector,
    useModal,
} from '../utils';

export default () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { openModal, closeModal } = useModal();
    const switchToModuleId = useAppSelector(state => state.capApplications.appControllerInfo.switchToModuleId);
    const encHouseHoldId = sessionStorage.getItem('encHouseHoldId');
    const dispatch: Dispatch<any> = useAppDispatch();
    const actions = { setError, startSpinner, stopSpinner };
    const isState = useSelector((state: any) => state?.common?.environment?.isState);
    const hasBackgroundColor: boolean = isState.ID;

    const responseCheck = response => {
        if (response?.message?.toLowerCase() === RequestStatus.SUCCESS || response === RequestStatus.SUCCESS) {
            return true;
        }
        return false;
    };

    const handlePostApiRequest = async (url, params) => {
        try {
            const response = await api.post(url, params);

            if (typeof response?.data === 'string') {
                if (response?.data?.trim() === 'success') {
                    return response.data;
                }

                try {
                    const parsedResponse = JSON.parse(response.data);
                    return parsedResponse;
                } catch (jsonError) {
                    console.error('JSON parsing error:', jsonError);
                    return response.data;
                }
            } else {
                return response?.data;
            }
        } catch (error) {
            console.error('handlePostApiRequest error:', error.message);
            throw new Error(`Request failed: ${error.message}`);
        }
    };

    const addOverrideComment = async (caseNumber: string, overrideComment: string) => {
        const payload = {
            caseNumber: caseNumber,
            overrideComment: overrideComment,
        };
        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_OVERRIDE_COMMENT.processReplacements({ encHouseHoldId }),
            payload,
        );

        return response;
    };

    const submitOverrideApplicationEditLock = async (caseNumber: string, comments: string, applicationType: string) => {
        const payload = {
            caseNumber: caseNumber,
            overrideCommentText: comments,
            applicationType: applicationType,
        };

        const response = await handlePostApiRequest(endpoints.MEMBER_APP_LIST_OVERRIDE_APPLICATION_LOCK, payload);

        if (
            response?.message?.toLowerCase() === RequestStatus.SUCCESS ||
            response?.status?.toLowerCase() === RequestStatus.SUCCESS
        ) {
            closeModal('csrOverrideCommentModal');
            navigate(location.pathname);
        }
    };

    const submitEndSepModal = async (caseNumber: string, commentText: string) => {
        const payload = {
            casenumber: caseNumber,
            commentText: commentText,
        };
        const response = await handlePostApiRequest(endpoints.MEMBER_APP_LIST_OVERRIDE_APPLICATION_END_SEP, payload);

        if (
            response?.message?.toLowerCase() === RequestStatus.SUCCESS ||
            response?.status?.toLowerCase() === RequestStatus.SUCCESS
        ) {
            closeModal('csrOverrideCommentModal');
            navigate(location.pathname);
            return RequestStatus.SUCCESS;
        }
        return RequestStatus.FAILURE;
    };

    const showErrorModal = (headerId: string) => {
        openModal('notifyModal', {
            headerId: headerId,
            bodyContentId: 'cap.applications.modal.csrsubmissionfailtitle',
            bodyMsgId: 'cap.applications.modal.srsubmissionfailcontent',
            bodyMsgId2: 'cap.applications.modal.csrsubmissionfailcontent2',
            buttonId: 'button.ok',
        });
    };

    const showSuccessModal = (
        headerId: string,
        coverageYear?: string,
        bodyContent: string = 'cap.applications.modal.applicationscsrsubmissionsucceed',
    ) => {
        openModal('notifyModal', {
            headerId: headerId,
            bodyContentId: bodyContent,
            bodyMsgId: 'cap.applications.modal.csroktogoback',
            buttonId: 'button.ok',
            coverageYear: coverageYear,
        });
    };

    const terminatePlan = async (caseNumber: string, terminationDate: string) => {
        const termDate = convertDateFormat(terminationDate);

        const payload = {
            caseNumber: caseNumber,
            coverageStartDate: '',
            terminationDate: termDate,
            reasonCode: REASON_CODE,
        };

        const response = await handlePostApiRequest(endpoints.MEMBER_APP_LIST_TERMINATE_PLAN, payload);
        if (responseCheck(response)) {
            closeModal('cancelTerminatePlanModal');
            showSuccessModal('cap.applications.modal.cancelorterminatemyplan2');
            return;
        } else {
            closeModal('cancelTerminatePlanModal');
            showErrorModal('cap.applications.modal.cancelorterminatemyplan2');
        }
    };

    const submitReverseDenial = async (caseNumber: string, enrollmentEndDate: Date | null, coverageYear) => {
        const payload = {
            caseNumber: caseNumber,
            enrollmentEndDate: dateMethods.formatDateToMMDDYYYY(enrollmentEndDate),
        };
        const response = await handlePostApiRequest(endpoints.MEMBER_APP_LIST_REVERSE_DENIAL, payload);
        if (responseCheck(response)) {
            closeModal('overrideSepQepDenialModal');
            showSuccessModal('cap.applications.modal.overridesepdenial', coverageYear);
            return;
        } else {
            closeModal('overrideSepQepDenialModal');
            showErrorModal('cap.applications.modal.overridesepdenial');
        }
    };

    const checkActive = async (appCoverageDate: number, caseNumber: string) => {
        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_CHECK_ACTIVE.processReplacements({ appCoverageDate: appCoverageDate }),
            null,
        );
        if (responseCheck(response)) {
            closeModal('csrOverrideCommentModal');
            openModal('notifyModal', {
                headerId: 'cap.applications.modal.overridesepdenial',
                bodyContentId: 'cap.application.modal.overridesepdenialactivemsg',
                buttonId: 'button.close',
            });
            return;
        } else {
            closeModal('csrOverrideCommentModal');
            openModal('overrideSepQepDenialModal', { caseNumber, coverageYear: appCoverageDate });
        }
    };

    const reruneligibility = async (caseNumber: string) => {
        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_RERUN_ELIGIBILITY.processReplacements({ caseNumber }),
            null,
        );
        if (responseCheck(response)) {
            closeModal('csrOverrideCommentModal');
            showSuccessModal('cap.applications.modal.reruneligibility2');
            return;
        } else {
            closeModal('csrOverrideCommentModal');
            showErrorModal('cap.applications.modal.reruneligibility2');
        }
    };

    const initVerifications = async (caseNumber: string) => {
        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_INIT_VERIFICATIONS.processReplacements({ caseNumber }),
            null,
        );
        if (responseCheck(response)) {
            closeModal('csrOverrideCommentModal');
            showSuccessModal(
                'cap.applications.modal.initiateverifications2',
                '',
                'cap.applications.modal.csrprocesssubmitted',
            );
            return;
        } else {
            closeModal('csrOverrideCommentModal');
            showErrorModal('cap.applications.modal.initiateverifications2');
        }
    };

    const sendobat = async (caseNumber: string) => {
        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_SEND_OBAT.processReplacements({ caseNumber }),
            null,
        );
        if (responseCheck(response)) {
            closeModal('csrOverrideCommentModal');
            showSuccessModal('cap.applications.modal.sendObAt.title');
            return;
        } else {
            closeModal('csrOverrideCommentModal');
            showErrorModal('cap.applications.modal.sendObAt.title');
        }
    };

    const updateCarrier = async (caseNumber: string) => {
        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_UPDATE_CARRIER.processReplacements({ caseNumber }),
            null,
        );
        if (responseCheck(response)) {
            closeModal('csrOverrideCommentModal');
            showSuccessModal('cap.applications.modal.updatecarrier2');
            return;
        } else {
            closeModal('csrOverrideCommentModal');
            showErrorModal('cap.applications.modal.updatecarrier2');
        }
    };

    const editApplicationRedirect = async (caseNumber: string) => {
        const switchToModuleName = sessionStorage.getItem('switchToModuleName');
        const firstName = sessionStorage.getItem('householdFirstName');
        const lastName = sessionStorage.getItem('householdLastName');
        const switchToResourceName = `${firstName} ${lastName}`;
        const ssapUrl = '/indportal/editapplication?caseNumber=' + caseNumber;

        const url = `/hix/account/user/switchUserRole?switchToModuleName=${switchToModuleName}&switchToModuleId=${switchToModuleId}&switchToResourceName=${switchToResourceName}&switchToNonDefaultURL=${ssapUrl}`;
        window.location.href = url;
    };

    const handleEditApplication = async (caseNumber: string) => {
        const payload = { caseNumber: caseNumber };

        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_CLONE_APPLICATION_BY_CASENUMBER.processReplacements({ caseNumber }),
            payload,
        );

        if (response) {
            sessionStorage.isInImpersonationMode = true;
            sessionStorage.impersonateHHID = sessionStorage.householdId;
            editApplicationRedirect(response.data);
        }
    };

    const getSepDetails = async (caseNumber: string, typeOfCsrAction: string, coverageYear: number) => {
        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_GET_SEP_DETAILS.processReplacements({ caseNumber }),
            null,
        );

        const sepDetails: IGetSepDetails = response;
        if (sepDetails) {
            if (typeOfCsrAction === CsrOptionName.COVERAGE_DATE) {
                closeModal('csrOverrideCommentModal');
                openModal('changeCoverageStartDateModal', { sepDetails: sepDetails, coverageYear });
            } else if (typeOfCsrAction === CsrOptionName.SPECIAL_ENROLLMENT) {
                closeModal('csrOverrideCommentModal');
                openModal('changeOpenSpecialEnrollment', { sepDetails: sepDetails });
            }
        } else {
            if (typeOfCsrAction === CsrOptionName.COVERAGE_DATE) {
                closeModal('csrOverrideCommentModal');
                showErrorModal('cap.applications.modal.coverageDate.title');
                return;
            } else if (typeOfCsrAction === CsrOptionName.SPECIAL_ENROLLMENT) {
                closeModal('csrOverrideCommentModal');
                showErrorModal('cap.applications.modal.openspecialenorollment2');
            }
        }
    };

    const handleSaveSepDetails = async (sepDetails: IGetSepDetails, typeOfCsrAction: string) => {
        const response = await handlePostApiRequest(endpoints.MEMBER_APP_LIST_SAVE_SEP_DETAILS, sepDetails);
        if (responseCheck(response)) {
            if (typeOfCsrAction === CsrOptionName.COVERAGE_DATE) {
                closeModal('changeCoverageStartDateModal');
                showSuccessModal('cap.applications.modal.coverageDate.title');
                return;
            } else if (typeOfCsrAction === CsrOptionName.SPECIAL_ENROLLMENT) {
                closeModal('changeOpenSpecialEnrollment');
                showSuccessModal('cap.applications.modal.openspecialenorollment2');
            }
            return;
        } else {
            if (typeOfCsrAction === CsrOptionName.COVERAGE_DATE) {
                closeModal('csrOverrideCommentModal');
                showErrorModal('cap.applications.modal.coverageDate.title');
                return;
            } else if (typeOfCsrAction === CsrOptionName.SPECIAL_ENROLLMENT) {
                closeModal('csrOverrideCommentModal');
                showErrorModal('cap.applications.modal.openspecialenorollment2');
            }
        }
    };

    const handleGetOverrideEligibilityStartDate = async (caseNumber: string, coverageYear: number) => {
        const overrideEligibilityStartDateData: IEligibilityDateDetails = await API.getData(
            endpoints.MEMBER_APP_LIST_GET_ELIGIBILITY_DATE_DETAILS.processReplacements({
                caseNumber,
                coverageYear,
                encHouseHoldId,
            }),
            dispatch,
            actions,
            true,
        );

        if (overrideEligibilityStartDateData) {
            openModal('overrideAptcEligibilityStartDateModal', { overrideEligibilityStartDateData });
        } else {
            closeModal('overrideAptcEligibilityStartDateModal');
            showErrorModal('cap.applications.csroption.overrideAPTCEligibilityStartDate');
        }
    };

    const fetchProgrmeEligibility = async application => {
        const programEligibilityData: IProgramEligibility = await API.getData(
            endpoints.MEMBER_APP_LIST_PROGRAM_ELIGIBILITY.processReplacements({
                caseNumber: application.caseNumber,
            }),
            dispatch,
            actions,
            true,
        );

        if (programEligibilityData) {
            openModal('csrOverrideProgramEligibilityModal', { data: programEligibilityData, application });
        } else {
            console.error('Cannot get Programe Eligibility');
        }
    };

    const submitOverrideProgElig = async (application, comment, coverageYear, caseNumber) => {
        submitOverrideProgramEligibilityComment(comment, caseNumber);

        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_OVERRIDE_PROGRAM_ELIGIBILITY.processReplacements({
                caseNumber,
                coverageYear,
            }),
            application,
        );

        if (responseCheck(response)) {
            closeModal('csrOverrideProgramEligibilityModal');
            showSuccessModal('cap.applications.modal.overrideProgramEligibility.title', coverageYear);
            return;
        } else {
            closeModal('csrOverrideProgramEligibilityModal');
            showErrorModal('cap.applications.modal.overrideProgramEligibility.title');
        }
    };

    const submitOverrideProgramEligibilityComment = async (overrideComment, caseNumber) => {
        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_OVERRIDE_COMMENT.processReplacements({
                encHouseHoldId,
            }),
            { caseNumber, overrideComment },
        );
        if (responseCheck(response)) {
            return;
        }
    };

    const fetchSepEventDetails = async application => {
        const sepData: ISepDetails = await API.getData(
            endpoints.MEMBER_APP_LIST_SEP_EVENT_DETAILS.processReplacements({
                caseNumber: application.caseNumber,
                encHouseHoldId,
            }),
            dispatch,
            actions,
            true,
        );

        if (sepData) {
            if (sepData?.applicants) {
                sepData.applicants = sepData.applicants.sort((a, b) => a.personId - b.personId);
            }

            for (let i = 0; i < sepData.applicants.length; i++) {
                for (let j = 0; j < sepData.applicants[i].events.length; j++) {
                    sepData.applicants[i].events[j].previousEventId = sepData.applicants[i].events[j].eventId;
                    sepData.applicants[i].events[j].previousEventDate = sepData.applicants[i].events[j].eventDate;
                    sepData.applicants[i].events[j].previousEventNameLabel = sepData.applicants[i].events[j].eventLabel;
                }
            }
            sepData.caseNumber = application.caseNumber;
            sepData.isFinancial = application.isNonFinancial ? false : true;
            sepData.applicationType = application.isQep ? ApplicationType.QEP : ApplicationType.SEP;

            let promises: any = [];

            for (let i = 0; i < sepData.applicants.length; i++) {
                for (let j = 0; j < sepData.applicants[i].events.length; j++) {
                    promises.push(getSepEventDetailsAPICall(i, j));
                }
            }

            Promise.all(promises).then(
                function () {
                    openModal('csrOverrideEnrollmentModal', { data: sepData, application });
                },
                function () {
                    console.error('Cannot get Sep Event Details');
                    showErrorModal('cap.applications.modal.updatecarrier2');
                },
            );
        } else {
            showErrorModal('cap.applications.modal.updatecarrier2');
            console.error('Cannot get Sep Event Details');
        }

        async function getSepEventDetailsAPICall(aplicantIndex, eventIndex) {
            let displayOnUIValue = YesNo.Y;
            let eventChangeType = sepData.applicants[aplicantIndex].events[eventIndex].changeType;
            if (eventChangeType === 'OVERRIDES') {
                displayOnUIValue = YesNo.N;
            }

            let payload = {
                isFinancial: sepData.isFinancial ? YesNo.Y : YesNo.N,
                displayOnUI: displayOnUIValue,
                changeType: eventChangeType,
            };

            return new Promise((resolve, reject) => {
                handlePostApiRequest(endpoints.MEMBER_APP_LIST_GET_SEP_EVENTS_BY_APPLICATION_TYPE, payload)
                    .then(resp => {
                        const eventList: any[] = [];
                        const currentEventId = sepData.applicants[aplicantIndex].events[eventIndex].eventId;
                        let isCurrentEventContained = false;

                        if (resp?.sepEventses) {
                            resp?.sepEventses.forEach(item => {
                                const eventId = item._links.self.href.split('/').pop();

                                eventList.push({
                                    eventNameLabel: item.label,
                                    eventId: eventId,
                                    type: item.type,
                                    allowFuture: item.allowFutureEvent,
                                });

                                if (eventId === currentEventId) {
                                    isCurrentEventContained = true;
                                }
                            });
                        }

                        sepData.applicants[aplicantIndex].events[eventIndex].eventList = eventList;

                        if (
                            !isCurrentEventContained &&
                            sepData.applicants[aplicantIndex].events[eventIndex].displayOnUi === YesNo.Y
                        ) {
                            sepData.applicants[aplicantIndex].events[eventIndex].eventList.push({
                                eventNameLabel: sepData.applicants[aplicantIndex].events[eventIndex].eventLabel,
                                eventId: sepData.applicants[aplicantIndex].events[eventIndex].eventId,
                                type: sepData.applicants[aplicantIndex].events[eventIndex].type,
                            });
                        }

                        if (sepData.applicants[aplicantIndex].events[eventIndex].isPlanSelectionAllowed === YesNo.N) {
                            sepData.applicants[aplicantIndex]['showOnUI'] = YesNo.N;
                        } else {
                            sepData.applicants[aplicantIndex]['showOnUI'] = YesNo.Y;
                        }

                        resolve(sepData);
                    })
                    .catch(error => {
                        showErrorModal('cap.applications.modal.updatecarrier2');
                        reject(error);
                    });
            });
        }
    };

    const getSepDateDetails = async (sepData, caseNumber, callback, gracePeriod, application) => {
        let applicantUpdatedSepEvents: ISepApplicantsEvents[] = getApplicantEvents(sepData);
        const error = { sepEndDate: { isUpdated: false } };

        if (applicantUpdatedSepEvents.length === 0) {
            error.sepEndDate.isUpdated = true;
            const promise = API.getData(
                endpoints.MEMBER_APP_LIST_GET_SEP_PERIOD_DATES.processReplacements({ caseNumber }),
                dispatch,
                actions,
                true,
            );

            promise.then(
                function (resp) {
                    if (resp !== '') {
                        sepData.sepStartDate = resp.sepStartDate;
                        sepData.sepEndDate = resp.sepEndDate;

                        sepData.previousSepStartDate = sepData.sepStartDate;
                        sepData.previousSepEndDate = sepData.sepEndDate;
                        callback();
                        openModal('overrideSpecialEntrollmentDetailsModal', {
                            data: sepData,
                            applicantUpdatedSepEvents,
                            application,
                        });
                    } else {
                        callback();
                        showErrorModal('cap.applications.modal.updatecarrier2');
                    }
                },
                function (response) {
                    callback();
                    showErrorModal('cap.applications.modal.updatecarrier2');
                },
            );
        } else {
            error.sepEndDate.isUpdated = true;
            sepData.sepStartDate = getSepDate(SepDateType.MIN, sepData, gracePeriod);
            sepData.sepEndDate = getSepDate(SepDateType.MAX, sepData, gracePeriod);

            sepData.previousSepStartDate = sepData.sepStartDate;
            sepData.previousSepEndDate = sepData.sepEndDate;
            callback();
            openModal('overrideSpecialEntrollmentDetailsModal', {
                data: sepData,
                applicantUpdatedSepEvents,
                application,
            });
        }
    };

    const saveSepOverride = async (sepDetails, applicantUpdatedSepEvents, maCurrentCoverageYear) => {
        const data = {
            sepStartDate: sepDetails.sepStartDate,
            sepEndDate: sepDetails.sepEndDate,
            caseNumber: sepDetails.caseNumber,
            applicantEvents: applicantUpdatedSepEvents,
            overrideCommentText: sepDetails.overrideCommentText,
            coverageYear: maCurrentCoverageYear,
            applicationType: sepDetails.applicationType,
            isFinancial: sepDetails.isFinancial,
        };

        const response = await handlePostApiRequest(
            endpoints.MEMBER_APP_LIST_SAVE_SEP_OVERRIDE_DETAILS.processReplacements({
                encHouseHoldId,
            }),
            data,
        );

        if (responseCheck(response)) {
            closeModal('overrideSpecialEntrollmentDetailsModal');
            showSuccessModal('cap.applications.csroption.overrideSpecialEnrollment');
            return;
        } else {
            closeModal('overrideSpecialEntrollmentDetailsModal');
            showErrorModal('cap.applications.csroption.overrideSpecialEnrollment');
        }
    };

    return {
        handlePostApiRequest: useCallback(handlePostApiRequest, []),
        addOverrideComment: useCallback(addOverrideComment, []),
        checkActive: useCallback(checkActive, []),
        submitReverseDenial: useCallback(submitReverseDenial, []),
        reruneligibility: useCallback(reruneligibility, []),
        getSepDetails: useCallback(getSepDetails, []),
        handleSaveSepDetails: useCallback(handleSaveSepDetails, []),
        initVerifications: useCallback(initVerifications, []),
        sendobat: useCallback(sendobat, []),
        updateCarrier: useCallback(updateCarrier, []),
        submitOverrideApplicationEditLock: useCallback(submitOverrideApplicationEditLock, []),
        submitEndSepModal: useCallback(submitEndSepModal, []),
        terminatePlan: useCallback(terminatePlan, []),
        handleGetOverrideEligibilityStartDate: useCallback(handleGetOverrideEligibilityStartDate, []),
        fetchSepEventDetails: useCallback(fetchSepEventDetails, []),
        getSepDateDetails: useCallback(getSepDateDetails, []),
        fetchProgrmeEligibility: useCallback(fetchProgrmeEligibility, []),
        handleEditApplication: useCallback(handleEditApplication, []),
        saveSepOverride: useCallback(saveSepOverride, []),
        submitOverrideProgElig: useCallback(submitOverrideProgElig, []),
        hasBackgroundColor,
    };
};
